import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogTitle, DialogContent, TextField, DialogActions } from '@mui/material';
import { Formik } from 'formik';
import { FC, useState } from 'react';
import { generalStore } from 'src/stores/GeneralStore';
import { object, string, ref } from 'yup';
import { changePassword } from 'src/api/csApi';
import { useTranslation } from 'react-i18next';
interface Props {
  open: boolean;
  onClose: () => void;
}

export const ChangePasswordDialog: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const validationSchema = object({
    oldPassword: string().required(t('changePasswordDialog.oldPasswordRequired')),
    newPassword: string().required(t('changePasswordDialog.newPasswordRequired')).min(8, t('changePasswordDialog.newPasswordMinLength')),
    passwordConfirm: string()
      .oneOf([ref('newPassword')], t('changePasswordDialog.passwordConfirmNotMatch'))
      .required(t('changePasswordDialog.passwordConfirmRequired')),
  });

  const initialValues = {
    oldPassword: '',
    newPassword: '',
    passwordConfirm: '',
  };

  const handleSubmit = async (values: { oldPassword: string; newPassword: string; passwordConfirm: string }) => {
    try {
      setIsLoading(true);
      await changePassword(values.oldPassword, values.newPassword);
      onClose();
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        generalStore.setError(t('changePasswordDialog.oldPasswordIncorrect'));
      } else {
        generalStore.setError('Unknown error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <DialogTitle>{t('changePasswordDialog.headline')}</DialogTitle>
            <DialogContent>
              <TextField
                margin="normal"
                fullWidth
                id="oldPassword"
                label={t('changePasswordDialog.oldPassword')}
                type="password"
                variant="standard"
                {...formik.getFieldProps('oldPassword')}
                error={formik.touched.oldPassword && Boolean(formik.errors.oldPassword)}
                helperText={formik.touched.oldPassword && formik.errors.oldPassword}
              />
              <TextField
                margin="normal"
                fullWidth
                id="newPassword"
                label={t('changePasswordDialog.newPassword')}
                type="password"
                variant="standard"
                {...formik.getFieldProps('newPassword')}
                error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                helperText={formik.touched.newPassword && formik.errors.newPassword}
              />
              <TextField
                margin="normal"
                fullWidth
                id="passwordConfirm"
                label={t('changePasswordDialog.passwordConfirm')}
                type="password"
                variant="standard"
                {...formik.getFieldProps('passwordConfirm')}
                error={formik.touched.passwordConfirm && Boolean(formik.errors.passwordConfirm)}
                helperText={formik.touched.passwordConfirm && formik.errors.passwordConfirm}
              />
            </DialogContent>
            <DialogActions>
              <Button color="secondary" onClick={onClose}>
                {t('changePasswordDialog.cancel')}
              </Button>
              <LoadingButton
                loading={isLoading}
                type="submit"
                color="secondary"
                variant="contained"
                data-cy="change-password-confirm-button"
              >
                {t('changePasswordDialog.submit')}
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
